<script setup lang="ts">
import type { ComputedRef, Ref } from 'vue';
import { computed, onMounted, ref, watch } from 'vue';
import PanelPage from "../components/PanelPage.vue";
import { fetchParkById } from "@/remote/park";
import { type Client, fetchClients } from "@/remote/client";
import { useI18n } from "vue-i18n";
import { fetchAgencys } from "@/remote/agency";
import {
  type ContractInfo,
  type Invoice,
  fetchContractInfo,
  createContractInfo,
  updateContractInfo,
  fetchInvoices,
  createInvoice,
  updateInvoice,
} from "@/remote/contractinfo";

const useAlerts = ref(null);
const showSnackbar = ref(false);
const snackbar = ref("");
const { t } = useI18n({ inheritLocale: true, useScope: 'local' });
const loadingOverlay = ref(false);
const clients: Ref<Array<Client>> = ref([] as Array<Client>);
const selected_client_id = ref<number | null>(null);
const selected_client: ComputedRef<Client | null> = computed(() => {
  if (selected_client_id.value === null) {
    return null;
  }
  return clients.value.find((elem: Client) => elem.id == selected_client_id.value)!;
});

const client_selector_state: Ref<"fetching" | "choose_a_client" | "failed"> = ref("fetching");
const selector_label = computed<string>(() => t(client_selector_state.value));

function copy<T>(x: T) {
  return JSON.parse(JSON.stringify(x)) as T;
}

function idToColorHSL(id: number): string {
  const GOLDEN_ANGLE = 137.508; // degrees
  const hue = (id * GOLDEN_ANGLE) % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

type Agency = {
  id: number,
  name: string,
  address: string,
  postal_code: string,
  city: string,
  country_alpha2: string,
};

const agencies = ref<Agency[]>();

onMounted(async () => {
  document.title = 'Accounting';
  loadingOverlay.value = true;
  try {
    [agencies.value, clients.value] = await Promise.all([fetchAgencys(), fetchClients()]);
    client_selector_state.value = "choose_a_client";
  } catch (e) {
    (useAlerts!.value as any).errorAlert('Impossible to load parks: ', e);
    client_selector_state.value = "failed";
  } finally {
    loadingOverlay.value = false;
  }
});

type Column = {
  field: keyof Invoice;
  // label: string;
  label: ComputedRef<string>;
  type: "string" | "date" | "number" | "checkYN";
};

type ParkInfo = {
  id: number;
  name: string;
  number_of_connectors: number;
  installation_date: string;
  agency_id: number;
  agency_name: string | null;
};

const parks: Ref<Array<ParkInfo>> = ref([]);

async function fetch_parks_info(ids: Array<number>): Promise<Array<ParkInfo>> {
  try {
    return await Promise.all(ids.map(async (pid: number): Promise<ParkInfo> => {
      const park = await fetchParkById(pid);
      return {
        id: park.id,
        name: park.name,
        number_of_connectors: (park.chargingstations as Array<any>).length,
        installation_date: park.installation_date,
        agency_id: park.agency,
        agency_name: agencies.value?.find((e) => e.id = park.agency)?.name ?? null,
      };
    }));
  } catch (e: any) {
    if (e.response && e.response.status == 404) {
      return [];  // ?
    }
    return [];
  }
}

const number_of_parks = computed<number>(() => parks.value.length);
const number_of_connectors = computed<number>(
  () => parks.value.map((pi: ParkInfo) => pi.number_of_connectors).reduce((a, b) => a + b, 0)
);
const oldest_installation_date = computed<string>(() => {
  if (parks.value === null || parks.value.length === 0) {
    return "";
  }
  const cmp = (a: string, b: string) => Date.parse(a) > Date.parse(b) ? 1 : -1;
  return parks.value.map((p: ParkInfo) => p.installation_date).sort(cmp)[0];
});

const contract_info: Ref<ContractInfo | null> = ref(null);
const invoices: Ref<Array<Partial<Invoice>> | null> = ref(null);
let original_invoices: Array<Invoice> | null = null;
watch(selected_client_id, async () => {
  const clientId = selected_client_id.value;
  if (clientId === null) {
    contract_info.value = null;
    invoices.value = null;
    parks.value = [];
    original_invoices = null;
    return;
  }
  try {
    const [ci, inv, pk] = await Promise.all([
      fetchContractInfo(clientId),
      fetchInvoices(clientId),
      fetch_parks_info(selected_client.value?.parks ?? [])
    ]);
    contract_info.value = ci;
    invoices.value = inv.sort((a, b) => Date.parse(a.invoicing_date) - Date.parse(b.invoicing_date));
    parks.value = pk;
  } catch (e) {
    (useAlerts!.value as any).errorAlert('Failed to load client data: ', e);
    contract_info.value = null;
    invoices.value = null;
    parks.value = [];
  }
  original_invoices = invoices.value ? (invoices.value as Invoice[]).map(copy) : null;
});
watch(() => contract_info.value?.contract_type, () => {
  if (contract_info.value === null) {
    return;
  }
  const value = contract_info.value.contract_type;
  if ((value ?? null) === null) {
    return;
  }
  contract_info.value.contract_signed = value.startsWith("signed");
});

async function ci_submit() {
  if (selected_client_id.value === null) {
    console.error("selected client id was null, aborting");
    return;
  }
  if (contract_info.value === null) {
    console.error("selected contract info is null, aborting");
    return;
  }

  const payload = contract_info.value;
  const from = payload.validity_from ?? null;
  const to = payload.validity_to ?? null;
  if ((from === null) != (to === null)) {
    (useAlerts!.value as any).errorAlert("both \"Valid from\" and \"until\" or none of them date must be null");
    return;
  }
  if (from !== null && to !== null) {
    if (Date.parse(from) > Date.parse(to)) {
      (useAlerts!.value as any).errorAlert("\"Valid from\" must be before \"until\"");
      return;
    }
  }

  if (!payload.contract) {
    payload.contract_signed = false;
    payload.contract_interrupted = false;
    payload.self_renewable = false;
    payload.enable_tilt = false;
    payload.contract_type = "";
  }

  ci_save_loading.value = true;
  try {
    if (contract_info.value!.__new === true) {
      delete contract_info.value!.__new;
      contract_info.value = await createContractInfo(payload);
    } else {
      contract_info.value = await updateContractInfo(selected_client_id.value, payload);
    }
    snackbar.value = t('saved');
    showSnackbar.value = true;
  } catch (e: any) {
    console.error("exception: ", e.response);
    alert(`Error ${e.response.status}: ${JSON.stringify(e.response.data, null, 2)}`);
  } finally {
    ci_save_loading.value = false;
  }
}

const ci_save_loading = ref<boolean>(false);

async function grid_submit() {
  function alert(message: string) {
    (useAlerts!.value as any).errorAlert(message);
  }

  function blank(arg: any | undefined): arg is any {
    if ((arg ?? null) === null) {
      return true;
    }
    return arg === "";
  }

  if (invoices.value === null) {
    console.error("invoices is null");
    return;
  }
  const rows = invoices.value;
  if (original_invoices === null) {
    console.error("original invoices were null");
    return;
  }

  // Check for each row
  for (let i = 0; i < rows.length; i++) {
    const value = rows[i];
    if (blank(value.invoicing_date)) {
      alert(`row ${i + 1}: field 'invoicing date' must have a value`);
      return;
    }
    if (blank(value.invoice_number)) {
      alert(`row ${i + 1}: field 'invoice number' must have a value`);
      return;
    }
    if (value.invoice_number < 0) {
      alert(`row ${i + 1}: field 'invoice number' must be a positive value`);
      return;
    }
    if (blank(value.validity_from)) {
      alert(`row ${i + 1}: field 'validity from' must have a value`);
      return;
    }
    if (blank(value.validity_to)) {
      alert(`row ${i + 1}: field 'validity to' must have a value`);
      return;
    }
    if (Date.parse(value.validity_from as string) > Date.parse(value.validity_to as string)) {
      alert(`row ${i + 1}: field 'validity from' must represent a date before 'validity to'`);
      return;
    }
  }

  // Check concluded, now can push
  let n_updated = 0;
  grid_save_loading.value = true;
  try {
    const l0 = original_invoices?.length || 0;
    for (let i = 0; i < rows.length; ++i) {
      const payload = copy(rows[i]);
      if (i >= l0) {
        const created = await createInvoice(selected_client_id.value!, payload);
        invoices.value[i] = copy(created);
        original_invoices.push(created);
        n_updated += 1;
        continue;
      }
      const {
        invoicing_date: id1,
        invoice_number: in1,
        validity_from: vf1,
        validity_to: vt1
      } = rows[i];
      const {
        invoicing_date: id2,
        invoice_number: in2,
        validity_from: vf2,
        validity_to: vt2
      } = original_invoices[i];

      const updated = [id1, in1, vf1, vt1];
      const original = [id2, in2, vf2, vt2];

      let is_updated = false;
      for (let i = 0; i < updated.length; ++i) {
        if (updated[i] !== original[i]) {
          is_updated = true;
          n_updated += 1;
          break;
        }
      }

      if (is_updated) {
        const res = await updateInvoice(selected_client_id.value!, payload.id!, payload);
        invoices.value[i] = copy(res);
        original_invoices[i] = copy(res);
      }
    }

    if (n_updated > 0) {
      console.info(`${n_updated} invoices updated successfully`);
      snackbar.value = t('saved');
      showSnackbar.value = true;
    } else {
      console.info("no invoices modified nor added, no need to update");
    }
  } catch (e: any) {
    console.error("error: ", e.response);
    alert(`Update Failed\nError ${e.response.status}: ${JSON.stringify(e.response.data, null, 2)}`);
  } finally {
    grid_save_loading.value = false;
  }
}

const grid_save_loading = ref<boolean>(false);

function label(key: string) {
  return computed(() => t(key));
}

const excel_columns: Array<Column> = [
  { field: "invoicing_date", label: label("invoicing_date"), type: "date" },
  { field: "invoice_number", label: label("invoice_number"), type: "number" },
  { field: "validity_from", label: label("validity_from"), type: "date" },
  { field: "validity_to", label: label("validity_to"), type: "date" },
];

const grid = ref<any>(null);
const new_line = () => {
  if (invoices.value !== null) {
    grid.value.newRecord({
      contractinfo: selected_client_id.value,
      invoicing_date: "",
      invoice_number: 0,
      validity_from: "",
      validity_to: "",
    } as Invoice);
  }
};

// function export_as_xlsx() {
//   const format = 'xlsx'
//   const exportSelectedOnly = true
//   const park_name = selected_park.value!.name.replace(' ', '_');
//   const filename = `invoices_${park_name}`
//   grid.value.exportTable(format, exportSelectedOnly, filename)
// }
</script>

<template>
  <v-card>
    <PanelPage ref="useAlerts" :loading-overlay="loadingOverlay" md="12">
      <v-form @submit.prevent="ci_submit">
        <v-card :title="t('contracts')">
          <v-card-title>
            <v-autocomplete
              :label="selector_label"
              :items="clients"
              v-model="selected_client_id"
              item-title="name"
              item-value="id"
              hide-details
              density="compact"
            />
          </v-card-title>

          <template v-if="selected_client_id">
            <v-divider thickness="4"/>
            <!--~~ ~~ ~~ ~~ ~~ CLIENT INFO ~~ ~~ ~~ ~~ ~~-->
            <v-card-title>Info</v-card-title>
            <v-card-text>
              <p>{{ t('number_of_parks') }}: {{ number_of_parks }}</p>
              <div class="d-flex flex-row">
                <div style="border: 1px dotted gray">
                  <template v-for="p in parks">
                    <v-tooltip :text="p.agency_name ?? 'No Agency'" location="bottom">
                      <template v-slot:activator="{ props }">
                        <v-chip v-bind="props" density="compact" :color="idToColorHSL(p.agency_id)">{{
                            p.name
                          }}
                        </v-chip>
                      </template>
                    </v-tooltip>
                  </template>
                </div>
              </div>
              <p>{{ t('number_of_chargepoints') }}: {{ number_of_connectors }}</p>
              <p>{{ t('oldest_installation_date') }}: {{ oldest_installation_date }}</p>
              <p>{{ t('mail_of_the_client') }}: {{ selected_client!.email }}</p>
              <p>Pennylande Client ID: {{ selected_client!.pennylane_id }}</p>
            </v-card-text>

            <v-divider thickness="4"/>
            <!--~~ ~~ ~~ ~~ ~~ CONTRACT DETAILS ~~ ~~ ~~ ~~ ~~-->
            <v-card-title>{{ t('contract_details') }} '{{ selected_client!.name }}'</v-card-title>
            <template v-if="contract_info !== null">
              <v-container class="ma-0 pa-0">
                <v-row no-gutters>
                  <v-col>
                    <v-card-actions>
                      <v-radio-group v-model="contract_info.contract">
                        <v-radio :label="t('there_is')" :value="true"/>
                        <v-radio :label="t('there_isnt')" :value="false"/>
                      </v-radio-group>
                    </v-card-actions>
                    <v-card-actions>
                      <v-checkbox v-model="contract_info.contract_interrupted" :label="t('interrupted')"/>
                      <v-checkbox v-model="contract_info.self_renewable" :label="t('self_renewable')"/>
                      <v-checkbox v-model="contract_info.enable_tilt" :label="t('enable_tilt')"/>
                    </v-card-actions>
                  </v-col>
                  <template v-if="contract_info.contract">
                    <v-col>
                      <v-card-actions>
                        <v-radio-group v-model="contract_info.contract_type">
                          <v-radio
                            :label="t('sent_not_signed')"
                            :value="'sent_not_signed'"
                          />
                          <v-radio
                            :label="t('signed_pay_after_maint')"
                            :value="'signed_pay_after_maint'"
                          />
                          <v-radio
                            :label="t('signed_pay_before_maint')"
                            :value="'signed_pay_before_maint'"
                          />
                        </v-radio-group>
                      </v-card-actions>
                    </v-col>
                    <v-col>
                      <v-text-field
                        :label="t('valid_from')"
                        type="date"
                        v-model="contract_info.validity_from"
                        density="comfortable"
                        hide-details
                      />
                      <v-text-field
                        :label="t('until')"
                        type="date"
                        v-model="contract_info.validity_to"
                        density="comfortable"
                        hide-details
                      />
                      <v-text-field
                        type="number"
                        :label="`${t('amount_of_maintenance')} (EUR)`"
                        :model-value="contract_info.maintenance_amount_cent/100"
                        @update:model-value="(val) => {contract_info && (contract_info.maintenance_amount_cent = Number(val)*100)}"
                        density="comfortable"
                        hide-details
                      />
                      <v-text-field
                        type="number"
                        :label="`${t('amount_of_software')} (EUR)`"
                        :model-value="contract_info.software_amount_cent/100"
                        @update:model-value="(val) => {contract_info && (contract_info.software_amount_cent = Number(val)*100)}"
                        density="comfortable"
                        hide-details
                      />
                    </v-col>
                  </template>
                </v-row>
                <v-row>
                  <v-col>
                    <v-card-actions>
                      <v-textarea
                        :label="t('notes')"
                        v-model="contract_info.notes"
                        density="compact"
                        prepend-icon="mdi-note"
                      />
                    </v-card-actions>
                  </v-col>
                </v-row>
              </v-container>
            </template>
            <v-card-actions>
              <v-btn :text="t('save')" type="submit" :loading="ci_save_loading" variant="tonal"/>
            </v-card-actions>

            <!--~~ ~~ ~~ ~~ ~~ INVOICES ~~ ~~ ~~ ~~ ~~-->
            <template v-if="invoices !== null">
              <v-divider thickness="4"/>
              <v-card-title>{{ t('invoices') }} '{{ selected_client!.name }}'</v-card-title>
              <v-card-actions style="height: 450px">
                <vue-excel-editor ref="grid" v-model="invoices" height="400px">
                  <vue-excel-column
                    v-for="column of excel_columns"
                    :key="column.field"
                    :field="column.field"
                    :label="column.label.value"
                    :type="column.type"
                  />
                </vue-excel-editor>
              </v-card-actions>
              <v-card-actions>
                <v-btn :text="t('new_line')" @click="new_line"/>
                <v-btn :text="t('del')" @click="grid.deleteSelectedRecords()"/>
                <v-btn :text="t('save')" @click="grid_submit" :loading="grid_save_loading" variant="tonal"/>
              </v-card-actions>
            </template>
          </template>
        </v-card>
      </v-form>
    </PanelPage>
  </v-card>
  <v-snackbar
    v-model="showSnackbar"
    timer
  >
    {{ snackbar }}
    <template v-slot:actions>
      <v-btn
        color="pink"
        variant="text"
        @click="showSnackbar = false"
      >
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>

<i18n lang="yaml">
en:
  contracts: Contracts
  fetching: Fetching clients...
  choose_a_client: Choose a client
  failed: Fetch failed, try reloading the page
  number_of_parks: Number of parks
  number_of_chargepoints: Number of chargepoints
  mail_of_the_client: Client's email
  notes: Notes
  contract_details: Contract details for client
  there_is: There is a contract
  there_isnt: There is no contract
  sent_not_signed: Sent, not signed
  signed_pay_after_maint: Signed, payment after maintenance
  signed_pay_before_maint: Signed, payment before maintenance
  interrupted: Interrupted
  self_renewable: Self Renewable
  enable_tilt: TILT
  valid_from: Valid from
  until: Until
  amount_of_maintenance: Maintenance cost
  amount_of_software: Software cost
  oldest_installation_date: Oldest installation date
  invoices: Invoices for client
  invoicing_date: Invoicing date
  payment_date: Payment date
  invoice_number: Invoice number
  amount: Amount
  paid: Paid
  validity_from: Validity from
  validity_to: Validity to
  save: Save
  new_line: New line
  del: delete selected
  saved: Saved

it:
  contracts: Contratti
  fetching: Recupero dei clienti...
  choose_a_client: Scegli un cliente
  failed: Recupero fallito, prova a ricaricare la pagina
  number_of_parks: Numero di parchi
  number_of_chargepoints: Numero di connettori
  mail_of_the_client: Email del cliente
  notes: Note
  contract_details: Dettagli del contratto per il cliente
  there_is: C'è un contratto
  there_isnt: Non c'è nessun contratto
  sent_not_signed: Inviato, non firmato
  signed_pay_after_maint: Firmato, pagamento dopo la manutenzione
  signed_pay_before_maint: Firmato, pagamento prima della manutenzione
  interrupted: Interrotto
  self_renewable: Autorinnovabile
  enable_tilt: TILT
  valid_from: Valido dal
  until: Fino a
  amount_of_maintenance: Costo della manutenzione
  amount_of_software: Costo del software
  oldest_installation_date: Data di installazione più vecchia
  invoices: Fatture per il cliente
  invoicing_date: Data di fatturazione
  payment_date: Data di pagamento
  invoice_number: Numero fattura
  amount: Importo
  paid: Pagato
  validity_from: Validità dal
  validity_to: Validità fino a
  save: Salva
  new_line: Nuova riga
  del: elimina selezioni
  saved: Salvato

fr:
  contracts: Contrats
  fetching: Récupération des clients...
  choose_a_client: Choisir un client
  failed: Échec de la récupération, essayez de recharger la page
  number_of_parks: Nombre de parcs
  number_of_chargepoints: Nombre de points de charge
  mail_of_the_client: Email du client
  notes: Notes
  contract_details: Détails du contrat
  there_is: Il y a un contrat
  there_isnt: Il n'y a pas de contrat
  sent_not_signed: Envoyé, non signé
  signed_pay_after_maint: Signé, paiement après maintenance
  signed_pay_before_maint: Signé, paiement avant maintenance
  interrupted: Interrompu
  self_renewable: Auto-renouvelable
  enable_tilt: TILT
  valid_from: Valide à partir de
  until: Jusqu'à
  amount_of_maintenance: Coût de maintenance
  amount_of_software: Coût du logiciel
  oldest_installation_date: Date d'installation la plus ancienne
  invoices: Factures
  invoicing_date: Date de facturation
  payment_date: Date de paiement
  invoice_number: Numéro de facture
  amount: Montant
  paid: Payé
  validity_from: Validité à partir de
  validity_to: Validité jusqu'à
  save: Sauvegarder
  new_line: Nouvelle ligne
  del: Supprimer
  saved: Enregistré
</i18n>

<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
